import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CurrencySelector } from '../../components/CurrencySelector';
import { Logo } from '../../components/ui/logo';
import { ThemeToggle } from '../../components/ui/theme-toggle';

/**
 * Centred card for the link's entry states — the code challenge, a dead link.
 *
 * `html` has `overflow: hidden` app-wide, so this owns its own scrolling.
 */
export function JobCard({ children }: { children: ReactNode }) {
	return (
		<div className="scroll-thin h-full overflow-y-auto">
			<div className="flex min-h-full flex-col items-center justify-center px-4 py-12">
				<div className="w-full max-w-[500px]">
					<div className="mb-8 flex items-center justify-between">
						<Logo />
						<ThemeToggle />
					</div>
					<div className="rounded-[var(--r-3)] border border-line-1 bg-surface px-6 py-7">
						{children}
					</div>
				</div>
			</div>
		</div>
	);
}

/**
 * Full-width page for the job itself. No nav — the session reaches nothing
 * else — but the same currency selector as every app's top bar, since the
 * price form is filled in that currency.
 */
export function JobPageShell({ children }: { children: ReactNode }) {
	return (
		<div className="scroll-thin h-full overflow-y-auto">
			<div className="mx-auto w-full max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<div className="mb-8 flex items-center justify-between gap-3">
					<Logo />
					<div className="flex items-center gap-2">
						<CurrencySelector />
						<ThemeToggle />
					</div>
				</div>
				{children}
			</div>
		</div>
	);
}

export function JobNotice({ title, detail }: { title: string; detail?: string }) {
	return (
		<div className="flex items-start gap-3 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5">
			<span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-danger/10 text-status-danger">
				<AlertTriangle size={11} strokeWidth={2.75} />
			</span>
			<div className="min-w-0">
				<div className="text-sm font-medium text-text-1">{title}</div>
				{detail && <div className="mt-0.5 text-sm text-text-4">{detail}</div>}
			</div>
		</div>
	);
}

/** Terminal screen: the link was revoked or replaced. No form, no retry. */
export function DeadLink({ message }: { message?: string | null }) {
	return (
		<JobCard>
			<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">
				This link is no longer active
			</h1>
			{message && <p className="mt-2 text-sm text-text-3">{message}</p>}
			<p className="mt-4 text-sm text-text-4">
				Ask your contact at 05 Creation for a new link — they can send one at any time.
			</p>
		</JobCard>
	);
}
